import type OrdersBatch from '~/types/OrdersBatch';

import { type OrderSelection } from '~/types/Order';
import {
  type BatchEntry,
  type BatchShipment,
  type BatchSteps,
  type BatchTransaction,
} from '~/types/OrdersBatch';
import { type TrackingReference } from '~/utils/TrackingHelper/types';

import { ActionKeys } from './constants';

export interface FetchOrdersBatchPayload {
  batch: OrdersBatch;
  currentBatchEntries?: HashMap<number, BatchEntry>;
  currentShipments?: HashMap<string, BatchShipment>;
  currentTransactions?: HashMap<string, BatchTransaction>;
  trackingReference?: TrackingReference;
}

export const fetchOrdersBatchSuccess = (payload: FetchOrdersBatchPayload) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_BATCH_FETCH_SUCCESS,
  };

export const fetchOrdersBatchError = () =>
  <const>{
    type: ActionKeys.ORDERS_BATCH_FETCH_ERROR,
  };

export const pollBatchOrdersStart = (batchId: number, trackingReference?: TrackingReference) =>
  <const>{
    payload: {
      batchId,
      trackingReference,
    },
    type: ActionKeys.ORDERS_BATCH_POLL_START,
  };

export const pollBatchOrdersEnd = () =>
  <const>{
    type: ActionKeys.ORDERS_BATCH_POLL_END,
  };

export const pollBatchOrdersTimeout = () =>
  <const>{
    type: ActionKeys.ORDERS_BATCH_POLL_TIMEOUT,
  };

export const createBatchTransactions = (
  batchId: number,
  rates: string[],
  trackingReference: TrackingReference,
  batchEntryIds: number[],
) =>
  <const>{
    payload: {
      batchEntryIds,
      batchId,
      rates,
      trackingReference,
    },
    type: ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS,
  };

export const createBatchTransactionsSuccess = (rateCount: number) =>
  <const>{
    payload: {
      rateCount,
    },
    type: ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_SUCCESS,
  };

export const createBatchTransactionsError = (error: string) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_ERROR,
  };

export const changeBatchStep = (step: BatchSteps) =>
  <const>{
    payload: {
      step,
    },
    type: ActionKeys.ORDERS_BATCH_STEP_CHANGE,
  };

export const generatePackingSlipsFromBatch = () =>
  <const>{
    type: ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS,
  };

interface DownloadPackingSlipsPayload {
  slipUrl: string;
}

export const generatePackingSlipsFromBatchSuccess = (payload: DownloadPackingSlipsPayload) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_SUCCESS,
  };

export const generatePackingSlipsFromBatchError = (error: string) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_ERROR,
  };

export const generateAllLabels = () =>
  <const>{
    type: ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS,
  };

interface DownloadAllLabelsPayload {
  labelsUrl: string;
}

export const generateAllLabelsSuccess = (payload: DownloadAllLabelsPayload) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_SUCCESS,
  };

export const generateAllLabelsError = (error: string) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_ERROR,
  };

export const ordersFetchTriggered = (ordersFetched: string[]) =>
  <const>{
    ordersFetched,
    type: ActionKeys.ORDERS_BATCH_ORDERS_FETCH_TRIGGERED,
  };

export const createAndBuyBatch = (
  orderSelection: OrderSelection,
  rateObjectIds: string[],
  trackingReference: TrackingReference,
) =>
  <const>{
    payload: {
      orderSelection,
      rateObjectIds,
      trackingReference,
    },
    type: ActionKeys.ORDERS_BATCH_CREATE_AND_BUY,
  };

export const fetchTransactionData = (remainingLabels: number, labelRenewalDate: string) =>
  <const>{
    payload: {
      labelRenewalDate,
      remainingLabels,
    },
    type: ActionKeys.ORDERS_BATCH_TRANSACTION_DATA,
  };

export const confirmBatchEntryRecipient = ({
  orderObjectId,
  orderShipmentId,
}: {
  orderObjectId: string;
  orderShipmentId: number;
}) =>
  <const>{
    payload: {
      orderObjectId,
      orderShipmentId,
    },
    type: ActionKeys.ORDERS_BATCH_ENTRY_CONFIRM_RECIPIENT,
  };

export type OrdersBatchActions =
  | ReturnType<typeof changeBatchStep>
  | ReturnType<typeof confirmBatchEntryRecipient>
  | ReturnType<typeof createAndBuyBatch>
  | ReturnType<typeof createBatchTransactions>
  | ReturnType<typeof createBatchTransactionsError>
  | ReturnType<typeof createBatchTransactionsSuccess>
  | ReturnType<typeof fetchOrdersBatchError>
  | ReturnType<typeof fetchOrdersBatchSuccess>
  | ReturnType<typeof fetchTransactionData>
  | ReturnType<typeof generateAllLabels>
  | ReturnType<typeof generateAllLabelsError>
  | ReturnType<typeof generateAllLabelsSuccess>
  | ReturnType<typeof generatePackingSlipsFromBatch>
  | ReturnType<typeof generatePackingSlipsFromBatchError>
  | ReturnType<typeof generatePackingSlipsFromBatchSuccess>
  | ReturnType<typeof ordersFetchTriggered>
  | ReturnType<typeof pollBatchOrdersEnd>
  | ReturnType<typeof pollBatchOrdersStart>
  | ReturnType<typeof pollBatchOrdersTimeout>;
